import ExpandableFilterPanel from 'components/molecules/expandable-filter-panel';
import { useTranslation } from 'react-i18next';
import { useOverviewDashboardFilter } from 'infrastructure/hooks/rpm/overview-dashboard';
import ClinicSelectBox from 'components/molecules/clinic-select-box';
import TimezoneSelectBox from 'components/molecules/timezone-select-box';
import StateSelectBox from 'components/molecules/states-types-select-box';
import LanguageSelectBox from 'components/molecules/language-select-box';
import PatientSelectBox from 'components/molecules/patient-select-box';
import { DateFormatter } from 'infrastructure/functions';
import { DateFormats } from 'infrastructure/enums';
import InteractionStatusSelect from 'components/molecules/select/interactionStatus';
import InteractiveTimeSelect from 'components/molecules/select/interactiveTime';
import useUser from 'utils/useUser';
import AssigneeSelectBox from 'components/molecules/assignee-select-box';
import BooleanSelectBox from 'components/molecules/boolean-select-box';
import ReadingTypeSelectBox from 'components/molecules/reading-type-select-box';
import BaseRangeDatepicker from 'components/atoms/base-range-datepicker';
import BaseSelectBox from 'components/atoms/base-select-box';
import { type FC } from 'react';
import ClinicDropdownSelectBox from 'components/molecules/clinic-dropdown-select-box';
import { ParentClinicOption } from 'infrastructure/enums/parent-clinic';
import { interactionStatusRpmOptions } from 'constants/forms';
import AgencyDoctorSelectBox from 'components/molecules/agency-doctor-select-box';
import DoctorSelectBox from 'components/molecules/doctor-select-box';

import ItemWrapper from './item-wrapper';
import s from './styles.module.scss';
import { useRangePicker } from './use-range-picker';

import type { Option } from 'components/molecules/select/base';
import type { InteractionStatusEnum } from 'infrastructure/enums';

interface OverviewDashboardFilterProps {}

const OverviewDashboardFilter: FC<OverviewDashboardFilterProps> = () => {
  const {
    isPractice,
    isCriticalOrAtRiskAlertingRole,
    isSupervisorRole,
    hasClinicalRole,
    isFullyManagedAgency,
    isAgencyDoctor,
  } = useUser();
  const { filter, updateFilter, count, resetFilter } =
    useOverviewDashboardFilter();
  const { t } = useTranslation();
  const { getPresets, onDateChange } = useRangePicker(updateFilter);

  const disabledDate = (current: Date) => {
    if (!current) return false;

    const now = new Date();
    const isInFuture = current > now;
    const filterStartDate = new Date(filter.startTime);
    const isNotSameMonth = current.getMonth() !== filterStartDate.getMonth();
    const isNotSameYear =
      current.getFullYear() !== filterStartDate.getFullYear();

    return isInFuture || isNotSameMonth || isNotSameYear;
  };

  const hideAssignee = isPractice || isCriticalOrAtRiskAlertingRole;
  const hideInteractiveTime = isCriticalOrAtRiskAlertingRole;

  return (
    <ExpandableFilterPanel filtersCount={count} onReset={resetFilter}>
      <div className={s.row}>
        <ItemWrapper label={t('labels.dateRange')}>
          <BaseRangeDatepicker
            onCalendarChange={onDateChange}
            value={[new Date(filter.startTime), new Date(filter.endTime)]}
            withPresets
            disabledDate={disabledDate}
            isMaxDayToday
            presets={getPresets}
          />
        </ItemWrapper>
        <div className={s['combine-cell']}>
          <ItemWrapper label={t('labels.parentClinic')}>
            <ClinicDropdownSelectBox
              value={filter.parentClinicOption}
              onChange={(v) =>
                updateFilter({ parentClinicOption: v, clinicGuid: undefined })
              }
              allowClear
            />
          </ItemWrapper>
          <ItemWrapper label={t('labels.clinic')}>
            <ClinicSelectBox
              placeholder={t('labels.search')}
              value={filter.clinicGuid}
              onChange={(v) => {
                updateFilter({ clinicGuid: v, page: undefined });
              }}
              allowClear
              parentClinicsOnly={
                filter.parentClinicOption === ParentClinicOption.ParentClinics
              }
            />
          </ItemWrapper>
        </div>
        <div className={s['combine-cell']}>
          <ItemWrapper label={t('labels.patientTimezone')}>
            <TimezoneSelectBox
              value={filter.timezone}
              onChange={(e) => updateFilter({ timezone: e, page: undefined })}
              allowClear
              allowSearch
              mainlandTimezonesOnly
              withAll
            />
          </ItemWrapper>
          <ItemWrapper label={t('labels.clinicState')}>
            <StateSelectBox
              allowClear
              withAll
              value={filter.clinicState}
              onChange={(v) =>
                updateFilter({ clinicState: v, page: undefined })
              }
              allowSearch
              filterOption={(inputValue, option) => {
                return option?.record.name
                  ?.toLowerCase()
                  .startsWith(inputValue.toLowerCase());
              }}
            />
          </ItemWrapper>
          <ItemWrapper label={t('labels.language')}>
            <LanguageSelectBox
              value={filter.language}
              withAll
              englishSpanishOnly
              onChange={(e) => updateFilter({ language: e, page: undefined })}
              allowClear
              allowSearch
            />
          </ItemWrapper>
        </div>
      </div>

      <div className={s.row}>
        <ItemWrapper label={t('labels.patientFilter')}>
          <PatientSelectBox
            clinicGuid={filter.clinicGuid}
            placeholder={t('labels.searchPatient')}
            value={filter.patientGuid}
            onChange={(v) => updateFilter({ patientGuid: v, page: undefined })}
            allowClear
            labelRender={(el) => {
              const date = DateFormatter({
                date: el.record?.birthDate,
                format: DateFormats['MM-dd-yyyy'],
                toUTC: true,
              });
              return `${el.label} ${date}`;
            }}
          />
        </ItemWrapper>
        <div className={s['combine-cell']}>
          <InteractionStatusSelect<InteractionStatusEnum>
            value={(
              interactionStatusRpmOptions as Option<InteractionStatusEnum>[]
            ).find((v) => v.value === filter.interactionStatus)}
            onChange={(v) =>
              updateFilter({ interactionStatus: v?.value, page: undefined })
            }
            allowClear
          />
          {!hideInteractiveTime && (
            <ItemWrapper label={t('labels.interactiveTime')}>
              <InteractiveTimeSelect
                value={filter.interactiveTimeRange}
                onChange={(v) => {
                  updateFilter({
                    interactiveTimeRange: v,
                    page: undefined,
                  });
                }}
                allowClear
              />
            </ItemWrapper>
          )}
        </div>
        <div className={s['combine-cell']}>
          <ItemWrapper label={t('labels.readingType')}>
            <ReadingTypeSelectBox
              value={filter.readingType}
              onChange={(v) => updateFilter({ readingType: v })}
              allowClear
              withAll
            />
          </ItemWrapper>
          <ItemWrapper label={t('labels.activitiesType')}>
            <BooleanSelectBox
              trueLabel={t('labels.actioned')}
              falseLabel={t('labels.unaction')}
              value={filter.reviewed}
              onChange={(v) => updateFilter({ reviewed: v, page: undefined })}
              allowClear
            />
          </ItemWrapper>
          {!hideAssignee && (
            <ItemWrapper label={t('labels.assignee')}>
              <AssigneeSelectBox
                value={filter.assignee}
                onChange={(v) => updateFilter({ assignee: v, page: undefined })}
                showAll
                allowClear
              />
            </ItemWrapper>
          )}
        </div>
      </div>
      <div className={s.row}>
        {isSupervisorRole && (
          <>
            <ItemWrapper label={t('labels.reviewedReadingBy')}>
              <AgencyDoctorSelectBox
                value={filter.actionedByAgencyDoctorGuid}
                onChange={(guid) =>
                  updateFilter({ actionedByAgencyDoctorGuid: guid })
                }
                allowClear
              />
            </ItemWrapper>
          </>
        )}
        {/* {isAgencyDoctor && (
            <ItemWrapper label={`${t('labels.cro')} ${t('labels.status')}`}>
              <BooleanSelectBox
                value={filter.croStatus}
                onChange={(v) =>
                  updateFilter({ croStatus: v, page: undefined })
                }
                allowClear
              />
            </ItemWrapper>
          )} */}
        {isPractice && isFullyManagedAgency && (
          <ItemWrapper label={t('labels.reviewedByAgency')}>
            <BaseSelectBox
              value={filter.showActionedByAgency}
              onChange={(v) =>
                updateFilter({
                  showActionedByAgency: v,
                  page: undefined,
                })
              }
              allowClear
              options={[
                { value: 'see_all', label: t('labels.seeAll') },
                { value: 'hide_actioned', label: t('labels.hideActioned') },
                { value: 'show_actioned', label: t('labels.showActioned') },
              ]}
            />
          </ItemWrapper>
        )}
        <div className={s['combine-cell']}>
          <ItemWrapper label={t('labels.provider')}>
            <DoctorSelectBox
              allowClear
              clinicGuid={filter.clinicGuid}
              placeholder={t('labels.search')}
              roles={[]}
              value={filter.doctorGuid}
              onChange={(guid) =>
                updateFilter({
                  doctorGuid: guid,
                  page: undefined,
                })
              }
            />
          </ItemWrapper>
          <ItemWrapper label={t('labels.patientState')}>
            <StateSelectBox
              placeholder="All"
              allowClear
              withAll
              withMultiStateLicense={
                hasClinicalRole && isFullyManagedAgency && isAgencyDoctor
              }
              value={filter.patientState}
              onChange={(v) =>
                updateFilter({ patientState: v, page: undefined })
              }
              allowSearch
              filterOption={(inputValue, option) => {
                return option?.record.name
                  ?.toLowerCase()
                  .startsWith(inputValue.toLowerCase());
              }}
              mode="multiple"
              maxTagCount="responsive"
            />
          </ItemWrapper>
        </div>
      </div>
    </ExpandableFilterPanel>
  );
};
export default OverviewDashboardFilter;
